/**
 * Jotai atoms for order details drawer state management
 *
 * The drawer state is URL-driven:
 * - URL format: /:servicesProviderId/orders/:tabId/:orderId/:detailsTab
 * - When orderId and detailsTab params are present, drawer is open
 * - When orderId and detailsTab params are absent, drawer is closed
 *
 * Use the `useOrderNavigation` hook for all navigation operations.
 */

import { atom } from 'jotai';

import type { ActionItem, OrdersTableRow } from '@ekhodealer/ekho-common';

export enum OrderDetailsTab {
	PROGRESS = 'progress',
	DETAILS = 'details',
	PAYMENT_AND_FAI = 'payment-and-fai',
	DOCUMENTS = 'documents',
	TIMELINE = 'timeline',
}

// ============================================================================
// Selected Order State
// Stores the currently selected order for tab content components to access
// This is set by the OrderDetailsDrawer based on the URL params
// ============================================================================

export interface OrderDetailsDrawerState {
	selectedRow: OrdersTableRow | null;
}

export const orderDetailsDrawerAtom = atom<OrderDetailsDrawerState>({
	selectedRow: null,
});

// ============================================================================
// Action Item Navigation State
// Manages sub-navigation within the drawer for action item details
// ============================================================================

export type ActionItemNavViewId = 'root' | 'action-item-detail';

export interface ActionItemNavState {
	/** Current view ID */
	viewId: ActionItemNavViewId;
	/** Navigation history for back functionality */
	history: ActionItemNavViewId[];
	/** Currently selected action item for detail view */
	selectedActionItem: ActionItem | null;
	/** Title for the current view (used in drawer header) */
	currentTitle: string | null;
}

const defaultActionItemNavState: ActionItemNavState = {
	viewId: 'root',
	history: [],
	selectedActionItem: null,
	currentTitle: null,
};

// Main action item navigation state atom
export const actionItemNavAtom = atom<ActionItemNavState>(defaultActionItemNavState);
// ============================================================================
// Action Item Navigation Actions
// ============================================================================

// Reset action item navigation when drawer closes
export const resetActionItemNavAtom = atom(null, (get, set) => {
	set(actionItemNavAtom, defaultActionItemNavState);
});

// Navigate to action item detail view
export const navigateToActionItemAtom = atom(null, (get, set, actionItem: ActionItem) => {
	const currentState = get(actionItemNavAtom);
	set(actionItemNavAtom, {
		viewId: 'action-item-detail',
		history: [...currentState.history, currentState.viewId],
		selectedActionItem: actionItem,
		currentTitle: actionItem.config.title,
	});
});

// Navigate back to previous view
export const navigateBackAtom = atom(null, (get, set) => {
	const currentState = get(actionItemNavAtom);
	if (currentState.history.length === 0) {
		return;
	}

	const previousViewId = currentState.history[currentState.history.length - 1];
	const newHistory = currentState.history.slice(0, -1);

	set(actionItemNavAtom, {
		viewId: previousViewId,
		history: newHistory,
		// Clear selected action item when going back to root
		selectedActionItem: previousViewId === 'root' ? null : currentState.selectedActionItem,
		currentTitle: previousViewId === 'root' ? null : currentState.currentTitle,
	});
});

// ============================================================================
// Derived Atoms
// ============================================================================

// Derived atom: NavStack handler for the NavStack component
export const actionItemNavHandlerAtom = atom((get) => {
	const { viewId, history } = get(actionItemNavAtom);
	return {
		currentViewId: viewId,
		history,
	};
});
